import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cardsAPI, accountsAPI } from '../services/api';

// Safe import of notification context
let useNotification = () => ({
  showError: (msg) => console.error('Notification:', msg),
  showSuccess: (msg) => console.log('Notification:', msg)
});

try {
  // Try to import the notification context
  const { useNotification: actualUseNotification } = require('../contexts/NotificationContext');
  useNotification = actualUseNotification;
} catch (e) {
  console.warn('NotificationContext not available, using fallback');
}

// Modal component defined outside to prevent recreation on every render
const Modal = React.memo(({ isOpen, onClose, title, children }) => (
  <AnimatePresence>
    {isOpen && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">{title}</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {children}
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
));

const Cards = () => {
  const [activeTab, setActiveTab] = useState('cards');
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [formData, setFormData] = useState({});
  const [cardRequestData, setCardRequestData] = useState({});
  const [bankAccounts, setBankAccounts] = useState([]);
  const [cardTypes, setCardTypes] = useState([]);
  const [cardNetworks, setCardNetworks] = useState([]);
  
  // Call hooks unconditionally at the top level
  const notification = useNotification();

  // Fetch cards from API using centralized service
  const fetchCards = async () => {
    try {
      console.log('🔐 Fetching cards using cardsAPI service...');
      const response = await cardsAPI.getCards();
      
      console.log('✅ Cards API response:', response);
      
      if (response?.status === 'success' && response?.data?.cards) {
        setCards(response.data.cards);
        setError(null);
      } else {
        throw new Error(response?.message || 'Failed to fetch cards');
      }
    } catch (err) {
      console.error('❌ Cards fetch error:', err);
      setError(err.message || 'Failed to load cards');
      if (notification) {
        notification.showError('Failed to load cards. Please try again.', 'Error');
      }
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchCards(),
        fetchBankAccounts(),
        fetchCardTypes(),
        fetchCardNetworks()
      ]);
      setLoading(false);
    };
    loadData();
  }, []);

  // Fetch bank accounts for card application
  const fetchBankAccounts = async () => {
    try {
      const response = await accountsAPI.getAccounts();
      if (response?.status === 'success' && response?.data?.accounts) {
        setBankAccounts(response.data.accounts);
      }
    } catch (err) {
      console.error('Error fetching bank accounts:', err);
    }
  };

  // Fetch card types
  const fetchCardTypes = async () => {
    try {
      const response = await cardsAPI.getCardTypes();
      if (response?.status === 'success' && response?.data) {
        setCardTypes(response.data);
      }
    } catch (err) {
      console.error('Error fetching card types:', err);
    }
  };

  // Fetch card networks
  const fetchCardNetworks = async () => {
    try {
      // For now, we'll use static data since the API might not have this endpoint
      setCardNetworks([
        { value: 'visa', label: 'Visa' },
        { value: 'mastercard', label: 'Mastercard' },
        { value: 'amex', label: 'American Express' },
        { value: 'discover', label: 'Discover' }
      ]);
    } catch (err) {
      console.error('Error fetching card networks:', err);
    }
  };

  // Handle card application
  const handleApplyForCard = async (cardId) => {
    try {
      setLoading(true);
      const response = await cardsAPI.apply(cardId, {
        agreed_to_terms: true,
        delivery_address: '', // Will use user's default address
        express_delivery: false
      });

      if (response?.status === 'success') {
        await fetchCards(); // Refresh cards
        if (notification) {
          notification.showSuccess(response.message || 'Card application submitted successfully!', 'Success');
        }
      } else {
        throw new Error(response?.message || 'Failed to apply for card');
      }
    } catch (err) {
      console.error('Card application error:', err);
      setError(err.message || 'Failed to apply for card');
      if (notification) {
        notification.showError(err.message || 'Failed to apply for card', 'Error');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle new card request
  const handleRequestCard = useCallback(async (requestData) => {
    try {
      setLoading(true);
      const response = await cardsAPI.apply(requestData);

      if (response?.status === 'success') {
        await fetchCards(); // Refresh cards
        setShowModal(false);
        setCardRequestData({});
        if (notification) {
          notification.showSuccess(response.message || 'Card request submitted successfully!', 'Success');
        }
      } else {
        throw new Error(response?.message || 'Failed to request card');
      }
    } catch (err) {
      console.error('Card request error:', err);
      setError(err.message || 'Failed to request card');
      if (notification) {
        notification.showError(err.message || 'Failed to request card', 'Error');
      }
    } finally {
      setLoading(false);
    }
  }, [notification]);

  // Handle card actions
  const handleCardAction = async (cardId, action, data = {}) => {
    try {
      setLoading(true);
      let response;

      switch (action) {
        case 'lock':
        case 'unlock':
          response = await cardsAPI.toggleLock(cardId, { 
            action, 
            reason: data.reason || 'User requested' 
          });
          break;
        case 'report-lost':
        case 'report-stolen':
          response = await cardsAPI.reportLostStolen(cardId, { 
            type: action.replace('report-', ''), 
            description: data.description 
          });
          break;
        case 'update-limits':
          response = await cardsAPI.setLimits(cardId, data);
          break;
        case 'update-settings':
          response = await cardsAPI.updateSettings(cardId, data);
          break;
        case 'change-pin':
          response = await cardsAPI.changePin(cardId, data);
          break;
        default:
          throw new Error(`Unknown action: ${action}`);
      }

      if (response?.status === 'success') {
        await fetchCards(); // Refresh cards
        setShowModal(false);
        setFormData({});
        if (notification) {
          notification.showSuccess('Card action completed successfully', 'Success');
        }
      } else {
        throw new Error(response?.message || 'Action failed');
      }
    } catch (err) {
      console.error('Card action error:', err);
      setError(err.message || 'Action failed');
      if (notification) {
        notification.showError(err.message || 'Action failed', 'Error');
      }
    } finally {
      setLoading(false);
    }
  };

  const openModal = useCallback(async (type, card = null) => {
    setModalType(type);
    setSelectedCard(card);
    setShowModal(true);
    setFormData(card ? { ...card } : {});
    
    // Reset card request data when opening card request modal
    if (type === 'request-card') {
      setCardRequestData({});
    }

    // If opening card details modal, fetch full card details
    if (type === 'card-details' && card) {
      try {
        const response = await cardsAPI.getCard(card.id);
        if (response?.status === 'success' && response?.data?.card) {
          setSelectedCard(response.data.card);
        }
      } catch (err) {
        console.error('Failed to load card details:', err);
        if (notification) {
          notification.showError('Failed to load card details', 'Error');
        }
      }
    }
  }, [notification]);

  const getCardColor = useCallback((card) => {
    const colors = {
      'visa': 'bg-gradient-to-r from-blue-600 to-blue-800',
      'mastercard': 'bg-gradient-to-r from-red-600 to-red-800',
      'amex': 'bg-gradient-to-r from-green-600 to-green-800',
      'discover': 'bg-gradient-to-r from-orange-600 to-orange-800'
    };
    return colors[card.card_brand || card.card_network] || 'bg-gradient-to-r from-gray-600 to-gray-800';
  }, []);

  // Memoized form input handlers
  const handleCardRequestInputChange = useCallback((field, value) => {
    setCardRequestData(prev => ({ ...prev, [field]: value }));
  }, []);

  // Component for pending card applications
  const PendingCardComponent = ({ card }) => (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className="bg-white rounded-xl border-2 border-dashed border-gray-300 p-6 text-center space-y-4"
    >
      <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center">
        <div className="w-8 h-8 text-gray-400">
          {card.card_network === 'visa' ? (
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M2 5h20c1.1 0 2 .9 2 2v10c0 1.1-.9 2-2 2H2c-1.1 0-2-.9-2-2V7c0-1.1.9-2 2-2z"/>
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M2 5h20c1.1 0 2 .9 2 2v10c0 1.1-.9 2-2 2H2c-1.1 0-2-.9-2-2V7c0-1.1.9-2 2-2z"/>
            </svg>
          )}
        </div>
      </div>
      
      <div>
        <h3 className="text-lg font-semibold text-gray-900">{card.card_name}</h3>
        <p className="text-sm text-gray-500 capitalize">{card.card_type} • {card.card_network}</p>
      </div>
      
      <div className="text-left space-y-2">
        <p className="text-sm text-gray-600">{card.application?.description}</p>
        <div className="space-y-1">
          {card.application?.benefits?.map((benefit, index) => (
            <div key={index} className="flex items-center text-sm text-gray-600">
              <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              {benefit}
            </div>
          ))}
        </div>
      </div>
      
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => handleApplyForCard(card.id)}
        className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
      >
        Apply for Card
      </motion.button>
    </motion.div>
  );

  // Component for approved/active cards
  const ActiveCardComponent = ({ card }) => (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className={`relative w-full h-56 rounded-xl shadow-lg ${getCardColor(card)} p-6 text-white overflow-hidden cursor-pointer`}
      onClick={() => openModal('card-details', card)}
    >
      {/* Pending Approval Overlay for Inactive Cards */}
      {card.status === 'inactive' && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-20">
          <div className="text-center">
            <div className="w-12 h-12 mx-auto bg-yellow-500 rounded-full flex items-center justify-center mb-2">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-sm font-medium">Pending Approval</p>
            <p className="text-xs opacity-80">Awaiting admin activation</p>
          </div>
        </div>
      )}
      {/* Card Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 right-0 w-40 h-40 rounded-full bg-white -mt-20 -mr-20"></div>
        <div className="absolute bottom-0 left-0 w-32 h-32 rounded-full bg-white -mb-16 -ml-16"></div>
      </div>
      
      <div className="relative z-10 h-full flex flex-col justify-between">
        <div className="flex justify-between items-start">
          <div>
            <h3 className="text-lg font-semibold">{card.card_name}</h3>
            <p className="text-sm opacity-90">{card.bank_account.account_name}</p>
          </div>
          <div className="text-right">
            <div className="flex space-x-1">
              <div className="w-8 h-5 bg-white opacity-90 rounded"></div>
              <div className="w-8 h-5 bg-yellow-400 rounded"></div>
            </div>
          </div>
        </div>
        
        <div>
          <div className="mb-4">
            <div className="w-12 h-8 bg-yellow-400 rounded opacity-80 mb-2"></div>
          </div>
          
          <div className="space-y-2">
            <p className="text-2xl font-mono tracking-wider">{card.masked_number}</p>
            <div className="flex justify-between">
              <div>
                <p className="text-xs opacity-70">EXPIRES</p>
                <p className="text-sm font-semibold">{card.expiry_date}</p>
              </div>
              <div className="text-right">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  card.status === 'active' 
                    ? 'bg-green-500 text-white' 
                    : card.status === 'locked'
                    ? 'bg-red-500 text-white'
                    : 'bg-yellow-500 text-white'
                }`}>
                  {card.status.toUpperCase()}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );


  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading cards...</p>
        </div>
      </div>
    );
  }

  // Filter cards
  const pendingCards = cards.filter(card => card.application?.is_pending);
  const activeCards = cards.filter(card => !card.application?.is_pending);

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900">Card Management</h1>
        <p className="text-gray-600 mt-2">Apply for new cards or manage your existing cards</p>
        
        {/* Request Card Button */}
        <div className="mt-6">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => openModal('request-card')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium flex items-center space-x-2 mx-auto transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span>Request New Card</span>
          </motion.button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
          <button
            onClick={() => setError(null)}
            className="float-right font-bold"
          >
            ×
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex justify-center">
        <div className="bg-gray-100 p-1 rounded-lg">
          <button
            onClick={() => setActiveTab('cards')}
            className={`px-6 py-2 rounded-md font-medium transition-colors ${
              activeTab === 'cards'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            My Cards
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`px-6 py-2 rounded-md font-medium transition-colors ${
              activeTab === 'settings'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Card Settings
          </button>
        </div>
      </div>

      {/* Cards Tab */}
      {activeTab === 'cards' && (
        <div className="space-y-8">
          {/* Available Cards to Apply For */}
          {pendingCards.length > 0 && (
            <div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Available Cards</h2>
              <p className="text-gray-600 mb-6">Apply for these cards to get started with your banking experience.</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {pendingCards.map((card) => (
                  <PendingCardComponent key={card.id} card={card} />
                ))}
              </div>
          </div>
          )}

          {/* Active Cards */}
          {activeCards.length > 0 && (
            <div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Your Cards</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {activeCards.map((card) => (
              <div key={card.id} className="space-y-4">
                    <ActiveCardComponent card={card} />
                
                {/* Card Actions */}
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  {card.status === 'inactive' ? (
                    <div className="text-center py-4">
                      <div className="w-12 h-12 mx-auto bg-yellow-100 rounded-full flex items-center justify-center mb-3">
                        <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">Card is pending approval</p>
                      <p className="text-xs text-gray-500">Actions will be available once your card is activated by admin</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => handleCardAction(card.id, card.status === 'active' ? 'lock' : 'unlock')}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          card.status === 'active'
                            ? 'bg-red-100 text-red-700 hover:bg-red-200'
                            : 'bg-green-100 text-green-700 hover:bg-green-200'
                        }`}
                      >
                        {card.status === 'active' ? 'Lock Card' : 'Unlock Card'}
                      </button>
                      <button
                        onClick={() => openModal('change-pin', card)}
                        className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
                      >
                        Change PIN
                      </button>
                      <button
                        onClick={() => openModal('set-limits', card)}
                        className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-200 transition-colors"
                      >
                        Set Limits
                      </button>
                      <button
                        onClick={() => openModal('report-lost', card)}
                        className="px-3 py-2 bg-orange-100 text-orange-700 rounded-lg text-sm font-medium hover:bg-orange-200 transition-colors"
                      >
                        Report Lost
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

          {/* No cards message */}
          {cards.length === 0 && (
            <div className="text-center py-12">
              <div className="w-24 h-24 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No cards available</h3>
              <p className="text-gray-600">Contact customer service to get started with card applications.</p>
            </div>
          )}
        </div>
      )}

      {/* Card Settings Tab */}
      {activeTab === 'settings' && (
        <div className="space-y-6">
          <h2 className="text-2xl font-semibold text-gray-900">Card Settings</h2>
          
          {activeCards.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {activeCards.map((card) => (
                <div key={card.id} className="bg-white rounded-xl border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    {card.card_name} - {card.masked_number}
                  </h3>
                  
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <div>
                    <h4 className="font-medium text-gray-900">Transaction Alerts</h4>
                    <p className="text-sm text-gray-600">Get notified of all card transactions</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                        <input 
                          type="checkbox" 
                          checked={card.settings.transaction_alerts}
                          onChange={(e) => handleCardAction(card.id, 'update-settings', {
                            transaction_alerts: e.target.checked
                          })}
                          className="sr-only peer" 
                        />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
                
                <div className="flex justify-between items-center">
                  <div>
                    <h4 className="font-medium text-gray-900">International Transactions</h4>
                    <p className="text-sm text-gray-600">Allow transactions outside the US</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                        <input 
                          type="checkbox" 
                          checked={card.settings.international_transactions}
                          onChange={(e) => handleCardAction(card.id, 'update-settings', {
                            international_transactions: e.target.checked
                          })}
                          className="sr-only peer" 
                        />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
                
                <div className="flex justify-between items-center">
                  <div>
                    <h4 className="font-medium text-gray-900">Contactless Payments</h4>
                    <p className="text-sm text-gray-600">Enable tap-to-pay functionality</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                        <input 
                          type="checkbox" 
                          checked={card.settings.contactless_enabled}
                          onChange={(e) => handleCardAction(card.id, 'update-settings', {
                            contactless_enabled: e.target.checked
                          })}
                          className="sr-only peer" 
                        />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                    <div className="pt-4 border-t">
                      <h4 className="font-medium text-gray-900 mb-3">Spending Limits</h4>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Daily ATM Limit</label>
                          <div className="relative">
                            <span className="absolute left-3 top-3 text-gray-500">$</span>
                            <input
                              type="number"
                              value={formData[`daily_atm_limit_${card.id}`] || card.limits.daily_atm_limit}
                              onChange={(e) => setFormData({...formData, [`daily_atm_limit_${card.id}`]: e.target.value})}
                              className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
              </div>
            </div>

                <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Daily Purchase Limit</label>
                  <div className="relative">
                    <span className="absolute left-3 top-3 text-gray-500">$</span>
                    <input
                      type="number"
                              value={formData[`daily_purchase_limit_${card.id}`] || card.limits.daily_purchase_limit}
                              onChange={(e) => setFormData({...formData, [`daily_purchase_limit_${card.id}`]: e.target.value})}
                              className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                        </div>
                        
                        <button 
                          onClick={() => handleCardAction(card.id, 'update-limits', {
                            daily_atm_limit: formData[`daily_atm_limit_${card.id}`] || card.limits.daily_atm_limit,
                            daily_purchase_limit: formData[`daily_purchase_limit_${card.id}`] || card.limits.daily_purchase_limit
                          })}
                          className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          Update Limits
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-600">No active cards to configure. Apply for a card first.</p>
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      <Modal
        isOpen={showModal && modalType === 'card-details'}
        onClose={() => setShowModal(false)}
        title="Card Details"
      >
        {selectedCard && (
          <div className="space-y-6">
            {/* Card Front */}
            <div className={`relative w-full h-48 rounded-xl shadow-lg ${getCardColor(selectedCard)} p-6 text-white overflow-hidden`}>
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-0 right-0 w-32 h-32 rounded-full bg-white -mt-16 -mr-16"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 rounded-full bg-white -mb-12 -ml-12"></div>
              </div>
              
              <div className="relative z-10 h-full flex flex-col justify-between">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-semibold">{selectedCard.card_name}</h3>
                    <p className="text-sm opacity-90">{selectedCard.bank_account.account_name}</p>
                  </div>
                  <div className="text-right">
                    <div className="flex space-x-1">
                      <div className="w-6 h-4 bg-white opacity-90 rounded"></div>
                      <div className="w-6 h-4 bg-yellow-400 rounded"></div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <div className="mb-3">
                    <div className="w-10 h-6 bg-yellow-400 rounded opacity-80 mb-2"></div>
                  </div>
                  
                  <div className="space-y-2">
                    <p className="text-xl font-mono tracking-wider">
                      {selectedCard.full_details?.formatted_card_number || selectedCard.masked_number}
                    </p>
                    <div className="flex justify-between">
                      <div>
                        <p className="text-xs opacity-70">EXPIRES</p>
                        <p className="text-sm font-semibold">{selectedCard.expiry_date}</p>
                      </div>
                      <div>
                        <p className="text-xs opacity-70">CVV</p>
                        <p className="text-sm font-semibold">
                          {selectedCard.full_details?.cvv || '•••'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Card Back */}
            <div className={`relative w-full h-48 rounded-xl shadow-lg ${getCardColor(selectedCard)} text-white overflow-hidden`}>
              <div className="absolute inset-0">
                <div className="w-full h-12 bg-black bg-opacity-80 mt-6"></div>
              </div>
              
              <div className="relative z-10 p-6 pt-20">
                <div className="bg-white bg-opacity-20 h-8 rounded mb-4 flex items-center justify-end pr-4">
                  <span className="text-sm font-mono">
                    {selectedCard.full_details?.cvv || '•••'}
                  </span>
                </div>
                
                <div className="text-xs space-y-1 opacity-80">
                                      <p>For customer service call: 1-800-CAPITAL</p>
                  <p>This card is property of First Capital International</p>
                  <p>Member FDIC • Equal Housing Lender</p>
                </div>
              </div>
            </div>

            <div className="text-center">
              <button
                onClick={() => setShowModal(false)}
                className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={showModal && modalType === 'change-pin'}
        onClose={() => setShowModal(false)}
        title="Change PIN"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Current PIN</label>
            <input
              type="password"
              maxLength="4"
              value={formData.current_pin || ''}
              onChange={(e) => setFormData({...formData, current_pin: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">New PIN</label>
            <input
              type="password"
              maxLength="4"
              value={formData.new_pin || ''}
              onChange={(e) => setFormData({...formData, new_pin: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirm PIN</label>
            <input
              type="password"
              maxLength="4"
              value={formData.confirm_pin || ''}
              onChange={(e) => setFormData({...formData, confirm_pin: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => setShowModal(false)}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={() => handleCardAction(selectedCard.id, 'change-pin', formData)}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Change PIN
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showModal && modalType === 'report-lost'}
        onClose={() => setShowModal(false)}
        title="Report Lost Card"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Are you sure you want to report this card as lost? This will immediately deactivate the card.
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
            <textarea
              value={formData.description || ''}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows="3"
              placeholder="Describe when and where you lost the card..."
            />
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => setShowModal(false)}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={() => handleCardAction(selectedCard.id, 'report-lost', formData)}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Report Lost
            </button>
          </div>
        </div>
      </Modal>

      {/* Card Request Modal */}
      <Modal
        isOpen={showModal && modalType === 'request-card'}
        onClose={() => setShowModal(false)}
        title="Request New Card"
      >
        <form onSubmit={(e) => {
          e.preventDefault();
          handleRequestCard(cardRequestData);
        }} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Bank Account</label>
            <select
              value={cardRequestData.account_id || ''}
              onChange={(e) => handleCardRequestInputChange('account_id', e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select Bank Account</option>
              {bankAccounts.map(account => (
                <option key={account.id} value={account.id}>
                  {account.account_name} - {account.account_number} (${account.available_balance})
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Card Type</label>
              <select
                value={cardRequestData.card_type || ''}
                onChange={(e) => handleCardRequestInputChange('card_type', e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select Type</option>
                <option value="debit">Debit Card</option>
                <option value="credit">Credit Card</option>
                <option value="prepaid">Prepaid Card</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Card Brand</label>
              <select
                value={cardRequestData.card_brand || ''}
                onChange={(e) => handleCardRequestInputChange('card_brand', e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select Brand</option>
                {cardNetworks.map(network => (
                  <option key={network.value} value={network.value}>
                    {network.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Card Name</label>
            <input
              type="text"
              value={cardRequestData.card_name || ''}
              onChange={(e) => handleCardRequestInputChange('card_name', e.target.value)}
              placeholder="e.g., My Primary Card"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Credit Limit (Optional)</label>
              <input
                type="number"
                value={cardRequestData.credit_limit || ''}
                onChange={(e) => handleCardRequestInputChange('credit_limit', e.target.value)}
                placeholder="0.00"
                min="0"
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_primary"
                checked={cardRequestData.is_primary || false}
                onChange={(e) => handleCardRequestInputChange('is_primary', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="is_primary" className="ml-2 block text-sm text-gray-700">
                Set as Primary Card
              </label>
            </div>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="agree_terms"
              required
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="agree_terms" className="ml-2 block text-sm text-gray-700">
              I agree to the terms and conditions for card issuance
            </label>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={() => setShowModal(false)}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Requesting...' : 'Request Card'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Cards; 